import {CheckIcon} from "lucide-react";
import * as RPNInput from "react-phone-number-input";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import FlagComponent from "@/components/ui/FlagComponent";
import {ScrollArea} from "@/components/ui/scroll-area";
import useCountries from "@/hooks/useCountries";
import clsxm from "@/lib/clsxm";
import {type ReactNode, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

const COUNTRY_SELECTOR_MIN_WIDTH = 300;

type CountrySelectListProps = {
  excludeCountries?: readonly RPNInput.Country[];
  fitListToContent?: boolean;
  hidePhoneNumber?: boolean;
  hideSearch?: boolean;
  itemClassName?: string;
  listClassName?: string;
  onChange: (country: RPNInput.Country) => void;
  onlyCountries?: readonly RPNInput.Country[];
  prioritizedCountries?: readonly RPNInput.Country[];
  renderItemAccessory?: (country: RPNInput.Country) => ReactNode;
  selectedItemClassName?: string;
  value?: RPNInput.Country;
  width?: number;
};

const CountrySelectList = ({
  excludeCountries,
  fitListToContent,
  hidePhoneNumber,
  hideSearch,
  itemClassName,
  listClassName,
  onChange,
  onlyCountries,
  prioritizedCountries,
  renderItemAccessory,
  selectedItemClassName,
  value: selectedCountry,
  width,
}: CountrySelectListProps) => {
  const {t} = useTranslation("company_profile");
  const scrollAreaRef = useRef<HTMLDivElement>(null!);
  const [searchValue, setSearchValue] = useState("");
  const {allCountries} = useCountries();

  const visibleCountries = onlyCountries
    ? allCountries?.filter(c => c.value && onlyCountries.includes(c.value))
    : excludeCountries
      ? allCountries?.filter(c => c.value && !excludeCountries.includes(c.value))
      : allCountries;

  const prioritizedVisibleCountries = prioritizedCountries
    ? (visibleCountries?.filter(c => c.value && prioritizedCountries.includes(c.value)) ?? [])
    : [];
  const otherVisibleCountries = prioritizedCountries
    ? (visibleCountries?.filter(c => !c.value || !prioritizedCountries.includes(c.value)) ?? [])
    : (visibleCountries ?? []);

  const renderCountryItem = ({label, value}: {label: string; value?: RPNInput.Country}) =>
    value ? (
      <CommandItem
        key={value}
        className={clsxm(
          "gap-2 data-[selected='true']:cursor-pointer",
          itemClassName,
          value === selectedCountry && selectedItemClassName,
        )}
        onSelect={() => onChange(value)}>
        <FlagComponent country={value} countryName={label} />
        <span className="flex-1 text-sm">{label}</span>
        {!hidePhoneNumber && (
          <span className="text-foreground/50 text-sm">{`+${RPNInput.getCountryCallingCode(value)}`}</span>
        )}
        {renderItemAccessory?.(value)}
        <CheckIcon className={clsxm("ml-auto size-4", value === selectedCountry ? "opacity-100" : "opacity-0")} />
      </CommandItem>
    ) : null;

  return (
    <Command>
      {!hideSearch && (
        <CommandInput
          value={searchValue}
          onValueChange={value => {
            setSearchValue(value);
            setTimeout(() => {
              if (scrollAreaRef.current) {
                const viewportElement = scrollAreaRef.current.querySelector("[data-radix-scroll-area-viewport]");
                if (viewportElement) {
                  viewportElement.scrollTop = 0;
                }
              }
            }, 0);
          }}
          placeholder={t("search_country")}
        />
      )}
      <CommandList>
        <ScrollArea
          ref={scrollAreaRef}
          className={listClassName}
          viewportClassName="max-h-72"
          style={fitListToContent ? {minWidth: width ?? 0} : {width: Math.max(width ?? 0, COUNTRY_SELECTOR_MIN_WIDTH)}}>
          <CommandEmpty>{t("no_country")}</CommandEmpty>
          {prioritizedVisibleCountries.length > 0 && (
            <CommandGroup>{prioritizedVisibleCountries.map(renderCountryItem)}</CommandGroup>
          )}
          {prioritizedVisibleCountries.length > 0 && otherVisibleCountries.length > 0 && <CommandSeparator />}
          {otherVisibleCountries.length > 0 && (
            <CommandGroup>{otherVisibleCountries.map(renderCountryItem)}</CommandGroup>
          )}
        </ScrollArea>
      </CommandList>
    </Command>
  );
};

export default CountrySelectList;
